import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { mapServiceCategoryToApi } from "@/components/pages/analytics/front-desk/hooks/useServicesAnalytics";
import type { ServiceCategoryFilter } from "@/components/pages/analytics/front-desk/components/services/mockData";
import { toAnalyticsDateParam } from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import { analyticsServicesService } from "@/services/analytics-services-service";
import { analyticsTenantService } from "@/services/analytics-tenant-service";
import type { ServicesListResponse } from "@/types/analytics-services";
import {
  mapTenantServicesToHotelRows,
  type AdminHotelServicesRow,
} from "../utils/adminServicesMappers";
import { useAdminTenantId } from "./useAdminTenantId";

const MAX_TOP_SERVICES = 5;

function isDateRangeReady(dateRange: DateRange): boolean {
  return Boolean(
    toAnalyticsDateParam(dateRange.from) && toAnalyticsDateParam(dateRange.to),
  );
}

async function fetchHotelTopServices(
  hotelId: string,
  from: string,
  to: string,
  category: ReturnType<typeof mapServiceCategoryToApi>,
): Promise<ServicesListResponse | null> {
  try {
    return await analyticsServicesService.getList({
      hotelId,
      from,
      to,
      category,
      page: 1,
      pageSize: MAX_TOP_SERVICES,
    });
  } catch {
    return null;
  }
}

async function fetchAdminServicesData(
  from: string,
  to: string,
  category: ServiceCategoryFilter,
) {
  const apiCategory = mapServiceCategoryToApi(category);
  const servicesTable = await analyticsTenantService.getServicesTable({
    from,
    to,
  });

  const hotelLists = await Promise.all(
    servicesTable.rows.map((hotel) =>
      fetchHotelTopServices(hotel.hotelId, from, to, apiCategory),
    ),
  );

  const listsByHotelId = new Map(
    servicesTable.rows.map((hotel, index) => [
      hotel.hotelId,
      hotelLists[index],
    ]),
  );

  return mapTenantServicesToHotelRows(
    servicesTable.rows,
    listsByHotelId,
    category,
    MAX_TOP_SERVICES,
  );
}

export const adminServicesKeys = {
  all: ["admin-analytics-services"] as const,
  hotels: (
    tenantId: string,
    from: string,
    to: string,
    category: ServiceCategoryFilter,
  ) => [...adminServicesKeys.all, tenantId, from, to, category] as const,
};

export function useAdminServicesHotels(
  dateRange: DateRange,
  category: ServiceCategoryFilter,
) {
  const tenantId = useAdminTenantId();
  const from = toAnalyticsDateParam(dateRange.from) ?? "";
  const to = toAnalyticsDateParam(dateRange.to) ?? "";
  const isReady = Boolean(tenantId && isDateRangeReady(dateRange));

  const query = useQuery({
    queryKey: adminServicesKeys.hotels(tenantId ?? "", from, to, category),
    queryFn: () => fetchAdminServicesData(from, to, category),
    enabled: isReady,
  });

  const rows = useMemo<AdminHotelServicesRow[]>(
    () => query.data ?? [],
    [query.data],
  );

  return {
    tenantId,
    rows,
    isLoading: query.isLoading,
    isError: query.isError,
    isReady,
  };
}
